import type { RoomView } from "../../shared/game";
import { t } from "./i18n";
import { SITE_TUNING } from "./siteTuning";
import { setCapyShieldHue } from "./capyShield";
import { createVaultCapyShieldElement } from "./vaultCapyShield";

/** Private review timing belongs to the player's screen, never to crack clocks. */
export class VaultAnswerCover {
    private readonly button = document.createElement("button");
    private readonly capy = createVaultCapyShieldElement({ className: "vault-answer-cover-capy" });
    private readonly instruction = document.createElement("span");
    private key = "";
    private reviewUntil = 0;
    private pointerId: number | null = null;
    private heldKey: string | null = null;
    private concealedAfterHold = false;
    private frame: number | null = null;
    private timer: number | null = null;

    public constructor(private readonly root: HTMLElement) {
        this.button.type = "button";
        this.button.className = "vault-answer-cover";
        this.instruction.className = "vault-answer-cover-instruction";
        this.button.append(this.capy, this.instruction);
        this.button.addEventListener("pointerdown", this.startPointer);
        this.button.addEventListener("lostpointercapture", this.endPointer);
        this.button.addEventListener("keydown", this.startKey);
        this.button.addEventListener("keyup", this.endKey);
        this.button.addEventListener("blur", this.release);
        this.button.addEventListener("click", this.preventDefault);
        this.button.addEventListener("contextmenu", this.preventDefault);
        window.addEventListener("pointerup", this.endPointer);
        window.addEventListener("pointercancel", this.endPointer);
        window.addEventListener("blur", this.release);
        document.addEventListener("visibilitychange", this.onVisibility);
    }

    public sync(room: RoomView) {
        const vault = room.vaultBreakers;
        const reviewKey = vault?.currentVaultId
            ? `${room.roomCode}:${room.viewer.sessionId}:${vault.roundNumber}:${vault.currentVaultId}` : "";
        const storageKey = `ziarcadia:vault-answer-review:${room.roomCode}:${room.viewer.sessionId}`;
        if (!room.viewer.isHost && vault?.viewer && !vault.viewer.solved && reviewKey) {
            // A fresh game can reuse the same round and vault IDs. Observing an
            // unsolved vault re-arms its review without re-arming a solved reload.
            try {
                if (JSON.parse(window.sessionStorage.getItem(storageKey) ?? "null")?.key === reviewKey) {
                    window.sessionStorage.removeItem(storageKey);
                }
            } catch { /* Private browsing can deny session storage. */ }
        }
        const nextKey = !room.viewer.isHost && vault?.viewer?.solved && !vault.roundComplete && vault.currentVaultId
            ? reviewKey : "";
        if (nextKey !== this.key) {
            this.cancelPlayback();
            this.pointerId = null;
            this.heldKey = null;
            this.concealedAfterHold = false;
            this.key = nextKey;
            if (nextKey) {
                const now = Date.now();
                this.reviewUntil = now + SITE_TUNING.vaultBreakers.answerPrivacy.reviewMs;
                // One record per room/player prevents a reload from replaying the
                // review. A new vault gets a new window; passive updates do not.
                try {
                    const saved = JSON.parse(window.sessionStorage.getItem(storageKey) ?? "null");
                    if (saved?.key === nextKey && Number.isFinite(saved.reviewUntil)) {
                        this.reviewUntil = Math.min(saved.reviewUntil, this.reviewUntil);
                    }
                    window.sessionStorage.setItem(storageKey, JSON.stringify({ key: nextKey, reviewUntil: this.reviewUntil }));
                } catch { /* Private browsing can deny session storage. */ }
            }
        }
        if (!nextKey) {
            this.button.remove();
            this.setContentHidden(false);
            return;
        }
        setCapyShieldHue(this.capy, room.viewer.avatarHue);
        const label = t("vaultHoldToReviewAnswer");
        this.instruction.textContent = label;
        this.button.setAttribute("aria-label", label);
        if (!this.button.isConnected) this.root.append(this.button);
        this.update();
    }

    public destroy() {
        this.cancelPlayback();
        this.button.remove();
        this.setContentHidden(false);
        window.removeEventListener("pointerup", this.endPointer);
        window.removeEventListener("pointercancel", this.endPointer);
        window.removeEventListener("blur", this.release);
        document.removeEventListener("visibilitychange", this.onVisibility);
    }

    private setContentHidden(hidden: boolean) {
        const content = this.root.querySelector<HTMLElement>(".vault-player-shell");
        if (!content) return;
        content.inert = hidden;
        if (hidden) content.setAttribute("aria-hidden", "true");
        else content.removeAttribute("aria-hidden");
    }

    private update = () => {
        this.cancelPlayback();
        if (!this.key) return;
        const elapsed = Date.now() - this.reviewUntil;
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || !SITE_TUNING.vaultBreakers.motion.enabled;
        const progress = this.concealedAfterHold ? 1 : elapsed < 0 ? 0 : reduced ? 1 : Math.min(1, elapsed / SITE_TUNING.vaultBreakers.answerPrivacy.fadeMs);
        const held = this.pointerId !== null || this.heldKey !== null;
        const opacity = held ? 0 : progress * progress * (3 - 2 * progress);
        this.button.style.opacity = String(opacity);
        this.button.style.pointerEvents = elapsed >= 0 ? "auto" : "none";
        this.button.tabIndex = elapsed >= 0 ? 0 : -1;
        this.button.setAttribute("aria-hidden", elapsed >= 0 ? "false" : "true");
        this.button.classList.toggle("is-held", held);
        this.setContentHidden(progress === 1 && !held);
        if (elapsed < 0) {
            this.timer = window.setTimeout(this.update, -elapsed);
        } else if (progress < 1 && !held) {
            if (window.requestAnimationFrame) this.frame = window.requestAnimationFrame(this.update);
            else this.timer = window.setTimeout(this.update, 16);
        }
    };

    private cancelPlayback() {
        if (this.frame !== null) window.cancelAnimationFrame(this.frame);
        if (this.timer !== null) window.clearTimeout(this.timer);
        this.frame = null;
        this.timer = null;
    }

    private readonly preventDefault = (event: Event) => event.preventDefault();
    private readonly startPointer = (event: PointerEvent) => {
        if (Date.now() < this.reviewUntil || this.pointerId !== null ||
            (event.pointerType === "mouse" && event.button !== 0)) return;
        event.preventDefault();
        this.pointerId = event.pointerId;
        this.concealedAfterHold = true;
        if (event.isTrusted) this.button.setPointerCapture?.(event.pointerId);
        this.update();
    };
    private readonly endPointer = (event: PointerEvent) => {
        if (event.pointerId !== this.pointerId) return;
        this.pointerId = null;
        if (this.button.hasPointerCapture?.(event.pointerId)) this.button.releasePointerCapture(event.pointerId);
        this.update();
    };
    private readonly startKey = (event: KeyboardEvent) => {
        if (Date.now() < this.reviewUntil || ![" ", "Enter"].includes(event.key)) return;
        event.preventDefault();
        this.heldKey = event.key;
        this.concealedAfterHold = true;
        this.update();
    };
    private readonly endKey = (event: KeyboardEvent) => {
        if (event.key !== this.heldKey) return;
        event.preventDefault();
        this.heldKey = null;
        this.update();
    };
    private readonly release = () => {
        const pointerId = this.pointerId;
        this.pointerId = null;
        this.heldKey = null;
        if (pointerId !== null && this.button.hasPointerCapture?.(pointerId)) this.button.releasePointerCapture(pointerId);
        this.update();
    };
    private readonly onVisibility = () => {
        if (document.visibilityState === "hidden") this.release();
    };
}
